import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import {
  CheckCircle2,
  FileAudio,
  FileText,
  Sparkles,
  BrainCircuit,
  Users,
  BarChart2,
  Database
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type CustomModelGuideModalProps = {
  isOpen: boolean;
  onClose: () => void;
  contributionOnly?: boolean;
};

export const CustomModelGuideModal = ({
  isOpen,
  onClose,
  contributionOnly = false
}: CustomModelGuideModalProps) => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';

  // Semantic icon palette — theme-aware, kept semantic per icon meaning
  const iconColors = {
    info: getColour('activeText'),
    requirements: isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)',
    audio: isDark ? 'rgb(251,146,60)' : 'rgb(234,88,12)',
    success: isDark ? 'rgb(52,211,153)' : 'rgb(5,150,105)',
    contribute: isDark ? 'rgb(167,139,250)' : 'rgb(124,58,237)',
    context: isDark ? 'rgb(251,191,36)' : 'rgb(217,119,6)',
    threshold: isDark ? 'rgb(147,197,253)' : 'rgb(37,99,235)',
    storage: isDark ? 'rgb(156,163,175)' : 'rgb(75,85,99)'
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title={t(
        contributionOnly
          ? 'contribution-guide-title'
          : 'custom-model-guide-title'
      )}
      footerActions={[
        <Button
          key="close"
          onClick={onClose}
          label={t('got-it')}
          size="small"
          variant="primary"
        />
      ]}
    >
      <div className="space-y-4 text-sm">
        {!contributionOnly && (
          <>
            <div className="flex items-start gap-3">
              <Sparkles
                className="h-5 w-5 mt-0.5 flex-shrink-0"
                style={{ color: iconColors.info }}
              />
              <div>
                <h4 className="font-medium mb-1">
                  {t('what-are-custom-models')}
                </h4>
                <p style={{ color: getColour('inactiveText') }}>
                  {t('custom-model-guide-what-description')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText
                className="h-5 w-5 mt-0.5 flex-shrink-0"
                style={{ color: iconColors.requirements }}
              />
              <div>
                <h4 className="font-medium mb-1">{t('what-you-need')}</h4>
                <p style={{ color: getColour('inactiveText') }}>
                  {t('custom-model-guide-requirements')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileAudio
                className="h-5 w-5 mt-0.5 flex-shrink-0"
                style={{ color: iconColors.audio }}
              />
              <div>
                <h4 className="font-medium mb-1">
                  {t('only-have-audio-files')}
                </h4>
                <p style={{ color: getColour('inactiveText') }}>
                  {t('custom-model-guide-force-alignment')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle2
                className="h-5 w-5 mt-0.5 flex-shrink-0"
                style={{ color: iconColors.success }}
              />
              <div>
                <h4 className="font-medium mb-1">{t('after-training')}</h4>
                <p style={{ color: getColour('inactiveText') }}>
                  {t('custom-model-guide-after-training')}
                </p>
              </div>
            </div>

            <hr style={{ borderColor: getColour('border') }} />
          </>
        )}

        {/* Everything below describes contributing from the editors. */}
        <>
          <div className="flex items-start gap-3">
            <BrainCircuit
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              style={{ color: iconColors.contribute }}
            />
            <div>
              <h4 className="font-medium mb-1">
                {t('contribute-from-caption-editor')}
              </h4>
              <p style={{ color: getColour('inactiveText') }}>
                {t('contribute-from-caption-editor-description')}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Users
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              style={{ color: iconColors.context }}
            />
            <div>
              <h4 className="font-medium mb-1">
                {t('guide-why-context-matters')}
              </h4>
              <p style={{ color: getColour('inactiveText') }}>
                {t('guide-why-context-matters-description')}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <BarChart2
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              style={{ color: iconColors.threshold }}
            />
            <div>
              <h4 className="font-medium mb-1">
                {t('guide-training-threshold')}
              </h4>
              <p style={{ color: getColour('inactiveText') }}>
                {t('guide-training-threshold-description')}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Database
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              style={{ color: iconColors.storage }}
            />
            <div>
              <h4 className="font-medium mb-1">{t('guide-data-retention')}</h4>
              <p style={{ color: getColour('inactiveText') }}>
                {t('guide-data-retention-description')}
              </p>
            </div>
          </div>
        </>
      </div>
    </Modal>
  );
};
